import { useState, useMemo, useRef } from 'react';
import Sidebar from '../../components/feature/Sidebar';
import { useFinanceRecords } from '../../store/financeStore';
import { useCustomers } from '../../store/customerStore';
import MonthlyReportModal from './components/MonthlyReportModal';
import CustomerReportModal from './components/CustomerReportModal';
import QualityReportModal from './components/QualityReportModal';

// 月份标签
const MONTHS = ['1月','2月','3月','4月','5月','6月','7月','8月','9月','10月','11月','12月'];

// 期初库存（与报送模块保持一致）
const OPENING_STOCK: Record<string, number> = {
  小麦: 1200, 玉米: 2500, 稻谷: 800, 大米: 600, 大豆: 300, 高粱: 150,
};

export default function Reports() {
  const { records: financeRecords } = useFinanceRecords();
  const { customers } = useCustomers();

  const [reportType, setReportType] = useState('monthly');
  const [selectedMonth, setSelectedMonth] = useState('2026-02');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [annualModalOpen, setAnnualModalOpen] = useState(false);
  const [annualYear, setAnnualYear] = useState('2026');
  const [exportingPDF, setExportingPDF] = useState(false);
  const annualContentRef = useRef<HTMLDivElement>(null);

  // 新增：购销存月报表 & 客户往来统计表弹窗
  const [monthlyModalOpen, setMonthlyModalOpen] = useState(false);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [qualityModalOpen, setQualityModalOpen] = useState(false);

  // ===== 年度汇总数据计算 =====
  const annualData = useMemo(() => {
    const year = annualYear;

    // 按月份聚合
    const monthlyMap: Record<number, {
      purchase: number; purchaseAmt: number;
      sales: number; salesAmt: number;
      tax: number; subsidy: number; cost: number;
    }> = {};
    for (let m = 1; m <= 12; m++) {
      monthlyMap[m] = { purchase: 0, purchaseAmt: 0, sales: 0, salesAmt: 0, tax: 0, subsidy: 0, cost: 0 };
    }

    financeRecords.forEach(r => {
      if (!r.date.startsWith(year)) return;
      const month = parseInt(r.date.split('-')[1], 10);
      if (!monthlyMap[month]) return;
      if (r.bizType === '收购' || r.type === '收购') {
        monthlyMap[month].purchase += r.quantity;
        monthlyMap[month].purchaseAmt += r.amount;
        monthlyMap[month].cost += r.operatingCost || r.amount;
        monthlyMap[month].subsidy += r.subsidyAmount || r.subsidy || 0;
      } else {
        monthlyMap[month].sales += r.quantity;
        monthlyMap[month].salesAmt += r.amount;
        monthlyMap[month].tax += r.taxAmount || r.tax || 0;
      }
    });

    // 按品种聚合
    const varietyMap: Record<string, {
      purchase: number; purchaseAmt: number;
      sales: number; salesAmt: number;
      tax: number; subsidy: number;
    }> = {};
    financeRecords.forEach(r => {
      if (!r.date.startsWith(year)) return;
      if (!varietyMap[r.variety]) {
        varietyMap[r.variety] = { purchase: 0, purchaseAmt: 0, sales: 0, salesAmt: 0, tax: 0, subsidy: 0 };
      }
      const v = varietyMap[r.variety];
      if (r.bizType === '收购' || r.type === '收购') {
        v.purchase += r.quantity;
        v.purchaseAmt += r.amount;
        v.subsidy += r.subsidyAmount || r.subsidy || 0;
      } else {
        v.sales += r.quantity;
        v.salesAmt += r.amount;
        v.tax += r.taxAmount || r.tax || 0;
      }
    });

    // 年度汇总
    const totalPurchase = Object.values(monthlyMap).reduce((s, m) => s + m.purchase, 0);
    const totalSales = Object.values(monthlyMap).reduce((s, m) => s + m.sales, 0);
    const totalRevenue = Object.values(monthlyMap).reduce((s, m) => s + m.salesAmt, 0);
    const totalCost = Object.values(monthlyMap).reduce((s, m) => s + m.cost, 0);
    const totalTax = Object.values(monthlyMap).reduce((s, m) => s + m.tax, 0);
    const totalSubsidy = Object.values(monthlyMap).reduce((s, m) => s + m.subsidy, 0);
    const totalGrossProfit = totalRevenue - totalCost;
    const totalNetProfit = totalGrossProfit - totalTax + totalSubsidy;

    // 期末库存（期初 + 收购 - 销售）
    const totalOpeningStock = Object.values(OPENING_STOCK).reduce((s, v) => s + v, 0);
    const closingStock = totalOpeningStock + totalPurchase - totalSales;

    return {
      monthly: monthlyMap,
      variety: varietyMap,
      totalPurchase, totalSales, totalRevenue, totalCost,
      totalTax, totalSubsidy, totalGrossProfit, totalNetProfit,
      totalOpeningStock, closingStock,
    };
  }, [financeRecords, annualYear]);

  // ===== 上一年度数据（同比分析） =====
  const prevYearData = useMemo(() => {
    const prevYear = String(parseInt(annualYear, 10) - 1);

    const monthlyMap: Record<number, {
      purchase: number; purchaseAmt: number;
      sales: number; salesAmt: number;
      tax: number; subsidy: number; cost: number;
    }> = {};
    for (let m = 1; m <= 12; m++) {
      monthlyMap[m] = { purchase: 0, purchaseAmt: 0, sales: 0, salesAmt: 0, tax: 0, subsidy: 0, cost: 0 };
    }

    const varietyMap: Record<string, {
      purchase: number; purchaseAmt: number;
      sales: number; salesAmt: number;
      tax: number; subsidy: number;
    }> = {};

    financeRecords.forEach(r => {
      if (!r.date.startsWith(prevYear)) return;
      const month = parseInt(r.date.split('-')[1], 10);
      if (!monthlyMap[month]) return;
      if (r.bizType === '收购' || r.type === '收购') {
        monthlyMap[month].purchase += r.quantity;
        monthlyMap[month].purchaseAmt += r.amount;
        monthlyMap[month].cost += r.operatingCost || r.amount;
        monthlyMap[month].subsidy += r.subsidyAmount || r.subsidy || 0;
      } else {
        monthlyMap[month].sales += r.quantity;
        monthlyMap[month].salesAmt += r.amount;
        monthlyMap[month].tax += r.taxAmount || r.tax || 0;
      }

      if (!varietyMap[r.variety]) {
        varietyMap[r.variety] = { purchase: 0, purchaseAmt: 0, sales: 0, salesAmt: 0, tax: 0, subsidy: 0 };
      }
      const v = varietyMap[r.variety];
      if (r.bizType === '收购' || r.type === '收购') {
        v.purchase += r.quantity; v.purchaseAmt += r.amount; v.subsidy += r.subsidyAmount || r.subsidy || 0;
      } else {
        v.sales += r.quantity; v.salesAmt += r.amount; v.tax += r.taxAmount || r.tax || 0;
      }
    });

    const totalPurchase = Object.values(monthlyMap).reduce((s, m) => s + m.purchase, 0);
    const totalSales = Object.values(monthlyMap).reduce((s, m) => s + m.sales, 0);
    const totalRevenue = Object.values(monthlyMap).reduce((s, m) => s + m.salesAmt, 0);
    const totalCost = Object.values(monthlyMap).reduce((s, m) => s + m.cost, 0);
    const totalTax = Object.values(monthlyMap).reduce((s, m) => s + m.tax, 0);
    const totalSubsidy = Object.values(monthlyMap).reduce((s, m) => s + m.subsidy, 0);
    const totalGrossProfit = totalRevenue - totalCost;
    const totalNetProfit = totalGrossProfit - totalTax + totalSubsidy;

    return {
      monthly: monthlyMap, variety: varietyMap,
      totalPurchase, totalSales, totalRevenue, totalCost,
      totalTax, totalSubsidy, totalGrossProfit, totalNetProfit,
    };
  }, [financeRecords, annualYear]);

  const handleGenerateReport = () => {
    setIsGenerating(true);
    setTimeout(() => setIsGenerating(false), 2000);
  };

  const handleExportReport = (format: string) => {
    setShowExportMenu(false);
    alert(`正在导出${format}格式报表...`);
  };

  const handleExportAnnualPDF = async () => {
    if (!annualContentRef.current) return;
    setExportingPDF(true);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const { jsPDF } = await import('jspdf');
      const element = annualContentRef.current;
      const canvas = await html2canvas(element, { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4'); // 横向A4
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 10;
      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight - 20;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight + 10;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight - 20;
      }
      pdf.save(`年度经营汇总表_${annualYear}年.pdf`);
    } catch {
      window.print();
    } finally {
      setExportingPDF(false);
    }
  };

  // 月度数据是否有业务
  const hasMonthData = (m: number) => {
    const d = annualData.monthly[m];
    return d && (d.purchase > 0 || d.sales > 0);
  };

  // 月度累计库存（期初 + 累计收购 - 累计销售）
  const getMonthClosingStock = (upToMonth: number) => {
    let stock = annualData.totalOpeningStock;
    for (let m = 1; m <= upToMonth; m++) {
      const d = annualData.monthly[m];
      stock += d.purchase - d.sales;
    }
    return Math.max(0, stock);
  };

  // 同比变化率计算辅助
  const getYoyRate = (current: number, prev: number) => {
    if (prev === 0 && current === 0) return { rate: 0, label: '—' };
    if (prev === 0) return { rate: 100, label: '+100%' };
    const rate = ((current - prev) / Math.abs(prev)) * 100;
    return { rate, label: `${rate >= 0 ? '+' : ''}${rate.toFixed(1)}%` };
  };

  const summaryData = {
    openingStock: 31500,
    totalPurchase: 2980,
    totalSales: 2240,
    closingStock: 32240
  };

  const varietyData = [
    { variety: '小麦', opening: 15200, purchase: 1100, sales: 900, closing: 15400 },
    { variety: '玉米', opening: 8650, purchase: 650, sales: 650, closing: 8650 },
    { variety: '大米', opening: 5230, purchase: 400, sales: 450, closing: 5180 },
    { variety: '大豆', opening: 2420, purchase: 830, sales: 240, closing: 3010 }
  ];

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      
      <div className="flex-1 ml-64">
        <div className="p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800">报表中心</h1>
            <p className="text-gray-500 mt-2">生成和导出粮食经营统计报表</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <i className="ri-settings-3-line mr-2 text-teal-600"></i>
              报表设置
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">报表类型</label>
                <select value={reportType} onChange={(e) => setReportType(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm transition-all">
                  <option value="daily">日报</option>
                  <option value="monthly">月报</option>
                  <option value="quarterly">季报</option>
                  <option value="yearly">年报</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">统计周期</label>
                <input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm transition-all" />
              </div>
              <div className="flex items-end">
                <button onClick={handleGenerateReport} disabled={isGenerating}
                  className="w-full px-6 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all text-sm font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center cursor-pointer">
                  {isGenerating ? (<><i className="ri-loader-4-line mr-2 animate-spin"></i>生成中...</>) : (<><i className="ri-file-chart-line mr-2"></i>生成报表</>)}
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
            {[
              { label: '期初库存', value: summaryData.openingStock, color: 'from-teal-500 to-teal-600', icon: 'ri-inbox-line' },
              { label: '本期收购', value: summaryData.totalPurchase, color: 'from-blue-500 to-blue-600', icon: 'ri-arrow-down-circle-line' },
              { label: '本期销售', value: summaryData.totalSales, color: 'from-amber-500 to-amber-600', icon: 'ri-arrow-up-circle-line' },
              { label: '期末库存', value: summaryData.closingStock, color: 'from-rose-500 to-rose-600', icon: 'ri-archive-line' },
            ].map(item => (
              <div key={item.label} className={`relative overflow-hidden bg-gradient-to-br ${item.color} rounded-xl p-6 text-white shadow-lg hover:shadow-xl transition-all`}>
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
                <div className="relative">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm opacity-90">{item.label}</p>
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                      <i className={`${item.icon} text-2xl`}></i>
                    </div>
                  </div>
                  <p className="text-3xl font-bold mb-1">{item.value.toLocaleString()}</p>
                  <p className="text-xs opacity-80">吨</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">分品种购销存汇总表</h3>
              <div className="relative">
                <button onClick={() => setShowExportMenu(!showExportMenu)}
                  className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium whitespace-nowrap flex items-center cursor-pointer">
                  <i className="ri-download-2-line mr-2"></i>导出报表<i className="ri-arrow-down-s-line ml-1"></i>
                </button>
                {showExportMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-10">
                    {[['Excel','ri-file-excel-2-line','text-green-600'],['CSV','ri-file-text-line','text-blue-600'],['PDF','ri-file-pdf-line','text-red-600']].map(([fmt, icon, color]) => (
                      <button key={fmt} onClick={() => handleExportReport(fmt)}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center cursor-pointer">
                        <i className={`${icon} mr-2 ${color}`}></i>导出为 {fmt}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    {['粮食品种','期初库存(吨)','本期收购(吨)','本期销售(吨)','期末库存(吨)','库存变化'].map((h, i) => (
                      <th key={h} className={`py-4 px-6 text-sm font-semibold text-gray-600 ${i === 0 ? 'text-left' : i === 5 ? 'text-center' : 'text-right'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {varietyData.map((item, index) => {
                    const change = item.closing - item.opening;
                    const changePercent = ((change / item.opening) * 100).toFixed(1);
                    const maxValue = Math.max(...varietyData.map(v => v.closing));
                    const barWidth = (item.closing / maxValue) * 100;
                    return (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="py-4 px-6 text-sm font-medium text-gray-800">{item.variety}</td>
                        <td className="py-4 px-6 text-sm text-right text-gray-800">{item.opening.toLocaleString()}</td>
                        <td className="py-4 px-6 text-sm text-right text-teal-600 font-semibold">{item.purchase.toLocaleString()}</td>
                        <td className="py-4 px-6 text-sm text-right text-blue-600 font-semibold">{item.sales.toLocaleString()}</td>
                        <td className="py-4 px-6 text-sm text-right font-semibold text-gray-800">
                          <div className="flex items-center justify-end space-x-3">
                            <div className="flex-1 max-w-[100px] h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full bg-gradient-to-r from-teal-400 to-teal-600 rounded-full transition-all" style={{ width: `${barWidth}%` }}></div>
                            </div>
                            <span>{item.closing.toLocaleString()}</span>
                          </div>
                        </td>
                        <td className="py-4 px-6 text-center">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${change > 0 ? 'bg-green-100 text-green-700' : change < 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                            {change > 0 ? '+' : ''}{change} ({changePercent}%)
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="bg-gray-50 font-semibold">
                    <td className="py-4 px-6 text-sm text-gray-800">合计</td>
                    <td className="py-4 px-6 text-sm text-right text-gray-800">{summaryData.openingStock.toLocaleString()}</td>
                    <td className="py-4 px-6 text-sm text-right text-teal-600">{summaryData.totalPurchase.toLocaleString()}</td>
                    <td className="py-4 px-6 text-sm text-right text-blue-600">{summaryData.totalSales.toLocaleString()}</td>
                    <td className="py-4 px-6 text-sm text-right text-gray-800">{summaryData.closingStock.toLocaleString()}</td>
                    <td className="py-4 px-6 text-center">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        +{summaryData.closingStock - summaryData.openingStock} (+{(((summaryData.closingStock - summaryData.openingStock) / summaryData.openingStock) * 100).toFixed(1)}%)
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <i className="ri-file-list-3-line mr-2 text-teal-600"></i>
                常用报表模板
              </h3>
              <div className="space-y-3">
                {[
                  { icon: 'ri-file-excel-2-line', color: 'teal', title: '购销存月报表', desc: '符合国家粮储局标准格式', preview: 'https://public.readdy.ai/ai/img_res/3f28b751dc22f1b9bda8aa27e1ec9960.jpg', onClick: () => setMonthlyModalOpen(true) },
                  { icon: 'ri-file-excel-2-line', color: 'blue', title: '客户往来统计表', desc: '按客户汇总交易明细', preview: 'https://readdy.ai/api/search-image?query=business%20customer%20transaction%20report%20spreadsheet%20template%20with%20client%20names%20amounts%20dates%20organized%20data%20table%20professional%20layout%20clean%20white%20background%20modern%20office%20document%20style%20realistic%20excel%20format&width=400&height=300&seq=report-template-2&orientation=landscape', onClick: () => setCustomerModalOpen(true) },
                  { icon: 'ri-file-excel-2-line', color: 'amber', title: '质量指标汇总表', desc: '水分、杂质等指标统计', preview: 'https://readdy.ai/api/search-image?query=quality%20control%20data%20spreadsheet%20template%20with%20moisture%20impurity%20percentage%20measurements%20scientific%20laboratory%20style%20clean%20organized%20table%20layout%20professional%20document%20white%20background%20modern%20analytical%20report&width=400&height=300&seq=report-template-3&orientation=landscape', onClick: () => setQualityModalOpen(true) },
                  { icon: 'ri-file-excel-2-line', color: 'rose', title: '年度经营汇总表', desc: '全年业务数据汇总', preview: 'https://readdy.ai/api/search-image?query=annual%20business%20summary%20report%20spreadsheet%20with%20yearly%20financial%20data%20charts%20graphs%20professional%20corporate%20document%20clean%20layout%20organized%20tables%20modern%20office%20style%20white%20background%20comprehensive%20overview&width=400&height=300&seq=report-template-4&orientation=landscape', onClick: () => setAnnualModalOpen(true) },
                ].map((template, idx) => (
                  <button key={idx} onClick={template.onClick}
                    className="group w-full flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-teal-300 hover:shadow-md transition-all cursor-pointer">
                    <div className="flex items-center space-x-3">
                      <div className={`relative w-16 h-12 bg-${template.color}-100 rounded-lg flex items-center justify-center overflow-hidden`}>
                        <img src={template.preview} alt={template.title} className="w-full h-full object-cover opacity-60" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <i className={`${template.icon} text-${template.color}-600 text-xl`}></i>
                        </div>
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-medium text-gray-800 group-hover:text-teal-600 transition-colors">{template.title}</p>
                        <p className="text-xs text-gray-500">{template.desc}</p>
                      </div>
                    </div>
                    <i className="ri-download-2-line text-gray-400 group-hover:text-teal-600 transition-colors"></i>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <i className="ri-information-line mr-2 text-teal-600"></i>
                报表说明
              </h3>
              <div className="space-y-4 text-sm text-gray-600">
                {[
                  { icon: 'ri-check-line', bg: 'bg-teal-100', color: 'text-teal-600', text: '所有报表格式完全符合《粮食经营台账管理办法》(国粮粮规〔2025〕437号)要求' },
                  { icon: 'ri-check-line', bg: 'bg-teal-100', color: 'text-teal-600', text: '报表数据自动从台账记录中汇总生成，确保数据准确性和一致性' },
                  { icon: 'ri-check-line', bg: 'bg-teal-100', color: 'text-teal-600', text: '支持导出为Excel格式，可直接用于向粮食主管部门报送' },
                  { icon: 'ri-check-line', bg: 'bg-teal-100', color: 'text-teal-600', text: '报表数据保存期限不少于3年，符合法规要求' },
                  { icon: 'ri-alert-line', bg: 'bg-amber-100', color: 'text-amber-600', text: '建议每月月末生成并导出报表，做好数据备份' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <div className={`w-6 h-6 ${item.bg} rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <i className={`${item.icon} ${item.color} text-sm`}></i>
                    </div>
                    <p>{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== 年度经营汇总表弹窗 ===== */}
      {annualModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm"
          onClick={() => setAnnualModalOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-[1100px] max-h-[92vh] overflow-hidden flex flex-col"
            onClick={e => e.stopPropagation()}>

            {/* 弹窗头部 */}
            <div className="flex items-center justify-between px-8 pt-6 pb-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-rose-50 rounded-xl flex items-center justify-center">
                  <i className="ri-file-excel-2-line text-xl text-rose-600"></i>
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-800">年度经营汇总表</h2>
                  <p className="text-xs text-gray-500 mt-0.5">全年粮食购进、销售、储存及财务数据汇总</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {/* 年份选择 */}
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-500 whitespace-nowrap">统计年度：</label>
                  <select value={annualYear} onChange={e => setAnnualYear(e.target.value)}
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 focus:ring-2 focus:ring-teal-400 focus:border-transparent cursor-pointer bg-white">
                    {['2026','2025','2024','2023'].map(y => <option key={y} value={y}>{y}年</option>)}
                  </select>
                </div>
                <button onClick={handleExportAnnualPDF} disabled={exportingPDF}
                  className="px-4 py-2 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-lg hover:shadow-md transition-all text-sm font-medium whitespace-nowrap cursor-pointer disabled:opacity-60 flex items-center">
                  {exportingPDF ? <><i className="ri-loader-4-line mr-1.5 animate-spin"></i>导出中...</> : <><i className="ri-file-pdf-2-line mr-1.5"></i>导出PDF</>}
                </button>
                <button onClick={() => window.print()}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer flex items-center">
                  <i className="ri-printer-line mr-1.5"></i>打印
                </button>
                <button onClick={() => setAnnualModalOpen(false)}
                  className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-gray-400">
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>

            {/* 弹窗内容 */}
            <div className="flex-1 overflow-y-auto px-8 py-6">
              <div ref={annualContentRef} className="space-y-6">

                {/* 文件标题 */}
                <div className="text-center border-b-2 border-gray-800 pb-5">
                  <h1 className="text-2xl font-bold text-gray-900 tracking-wider mb-2">
                    {annualYear} 年度粮食经营汇总表
                  </h1>
                  <div className="flex items-center justify-center space-x-6 text-sm text-gray-600 mt-3">
                    <span>企业名称：<strong className="text-gray-800">丰收粮食贸易有限公司</strong></span>
                    <span className="text-gray-300">|</span>
                    <span>统计年度：<strong className="text-gray-800">{annualYear} 年</strong></span>
                    <span className="text-gray-300">|</span>
                    <span>填报日期：<strong className="text-gray-800">{new Date().toLocaleDateString('zh-CN')}</strong></span>
                  </div>
                </div>

                {/* 年度核心指标卡片 */}
                <div className="grid grid-cols-4 gap-4">
                  {[
                    { label: '年度收购总量', value: `${annualData.totalPurchase.toLocaleString()} 吨`, sub: `收购金额 ¥${(Object.values(annualData.monthly).reduce((s,m)=>s+m.purchaseAmt,0)/10000).toFixed(1)}万`, icon: 'ri-arrow-down-circle-line', color: 'bg-teal-50 border-teal-200', iconColor: 'text-teal-600', valColor: 'text-teal-700' },
                    { label: '年度销售总量', value: `${annualData.totalSales.toLocaleString()} 吨`, sub: `营业收入 ¥${(annualData.totalRevenue/10000).toFixed(1)}万`, icon: 'ri-arrow-up-circle-line', color: 'bg-orange-50 border-orange-200', iconColor: 'text-orange-500', valColor: 'text-orange-600' },
                    { label: '年度毛利润', value: `¥${(annualData.totalGrossProfit/10000).toFixed(2)}万`, sub: `税后净利润 ¥${(annualData.totalNetProfit/10000).toFixed(2)}万`, icon: 'ri-money-cny-circle-line', color: 'bg-emerald-50 border-emerald-200', iconColor: 'text-emerald-600', valColor: 'text-emerald-700' },
                    { label: '年末库存', value: `${Math.max(0, annualData.closingStock).toLocaleString()} 吨`, sub: `期初库存 ${annualData.totalOpeningStock.toLocaleString()} 吨`, icon: 'ri-archive-line', color: 'bg-amber-50 border-amber-200', iconColor: 'text-amber-600', valColor: 'text-amber-700' },
                  ].map(item => (
                    <div key={item.label} className={`${item.color} border rounded-xl p-4`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-500 font-medium">{item.label}</span>
                        <div className={`w-8 h-8 flex items-center justify-center rounded-lg bg-white ${item.iconColor}`}>
                          <i className={`${item.icon} text-base`}></i>
                        </div>
                      </div>
                      <div className={`text-xl font-bold ${item.valColor} mb-1`}>{item.value}</div>
                      <div className="text-xs text-gray-400">{item.sub}</div>
                    </div>
                  ))}
                </div>

                {/* 一、月度购销存汇总 */}
                <div>
                  <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center">
                    <span className="w-6 h-6 bg-gray-800 text-white rounded text-xs flex items-center justify-center mr-2 font-bold">一</span>
                    月度购销存汇总表（单位：吨 / 元）
                  </h3>
                  <div className="overflow-x-auto rounded-xl border border-gray-200">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border-b border-gray-200 px-3 py-3 text-left text-gray-700 font-bold whitespace-nowrap">月份</th>
                          <th className="border-b border-gray-200 px-3 py-3 text-right text-gray-700 font-bold whitespace-nowrap">期初库存(吨)</th>
                          <th className="border-b border-gray-200 px-3 py-3 text-right text-gray-700 font-bold whitespace-nowrap">收购量(吨)</th>
                          <th className="border-b border-gray-200 px-3 py-3 text-right text-gray-700 font-bold whitespace-nowrap">收购金额(元)</th>
                          <th className="border-b border-gray-200 px-3 py-3 text-right text-gray-700 font-bold whitespace-nowrap">销售量(吨)</th>
                          <th className="border-b border-gray-200 px-3 py-3 text-right text-gray-700 font-bold whitespace-nowrap">营业收入(元)</th>
                          <th className="border-b border-gray-200 px-3 py-3 text-right text-gray-700 font-bold whitespace-nowrap">营业成本(元)</th>
                          <th className="border-b border-gray-200 px-3 py-3 text-right text-gray-700 font-bold whitespace-nowrap">应缴税款(元)</th>
                          <th className="border-b border-gray-200 px-3 py-3 text-right text-gray-700 font-bold whitespace-nowrap">政府补贴(元)</th>
                          <th className="border-b border-gray-200 px-3 py-3 text-right text-gray-700 font-bold whitespace-nowrap">期末库存(吨)</th>
                          <th className="border-b border-gray-200 px-3 py-3 text-center text-gray-700 font-bold whitespace-nowrap">备注</th>
                        </tr>
                      </thead>
                      <tbody>
                        {MONTHS.map((label, idx) => {
                          const m = idx + 1;
                          const d = annualData.monthly[m];
                          const prevClosing = getMonthClosingStock(m - 1);
                          const closing = getMonthClosingStock(m);
                          const noData = !hasMonthData(m);
                          const rowBg = noData
                            ? 'bg-gray-50 bg-opacity-60'
                            : idx % 2 === 0
                              ? 'bg-white'
                              : 'bg-gray-50 bg-opacity-30';
                          return (
                            <tr key={m} className={`border-b border-gray-100 ${rowBg} hover:bg-teal-50/20 transition-colors`}>
                              <td className="px-3 py-2.5 font-semibold text-gray-700 whitespace-nowrap">{annualYear}年{label}</td>
                              <td className="px-3 py-2.5 text-right text-gray-600">{prevClosing.toLocaleString()}</td>
                              <td className={`px-3 py-2.5 text-right font-medium ${noData ? 'text-gray-300' : 'text-teal-600'}`}>
                                {noData ? '—' : d.purchase.toLocaleString()}
                              </td>
                              <td className={`px-3 py-2.5 text-right ${noData ? 'text-gray-300' : 'text-gray-700'}`}>
                                {noData ? '—' : d.purchaseAmt.toLocaleString()}
                              </td>
                              <td className={`px-3 py-2.5 text-right font-medium ${noData ? 'text-gray-300' : 'text-orange-500'}`}>
                                {noData ? '—' : d.sales.toLocaleString()}
                              </td>
                              <td className={`px-3 py-2.5 text-right ${noData ? 'text-gray-300' : 'text-emerald-600 font-medium'}`}>
                                {noData ? '—' : d.salesAmt.toLocaleString()}
                              </td>
                              <td className={`px-3 py-2.5 text-right ${noData ? 'text-gray-300' : 'text-gray-700'}`}>
                                {noData ? '—' : d.cost.toLocaleString()}
                              </td>
                              <td className={`px-3 py-2.5 text-right ${noData ? 'text-gray-300' : 'text-amber-600'}`}>
                                {noData ? '—' : d.tax.toLocaleString()}
                              </td>
                              <td className={`px-3 py-2.5 text-right ${noData ? 'text-gray-300' : 'text-teal-600'}`}>
                                {noData ? '—' : d.subsidy.toLocaleString()}
                              </td>
                              <td className="px-3 py-2.5 text-right font-semibold text-gray-800">{closing.toLocaleString()}</td>
                              <td className="px-3 py-2.5 text-center text-gray-400 text-xs">
                                {noData ? <span className="text-gray-300 text-xs">本月无业务，结转上月数据</span> : ''}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                          <td className="px-3 py-3 text-gray-800">全年合计</td>
                          <td className="px-3 py-3 text-right text-gray-600">{annualData.totalOpeningStock.toLocaleString()}</td>
                          <td className="px-3 py-3 text-right text-teal-700">{annualData.totalPurchase.toLocaleString()}</td>
                          <td className="px-3 py-3 text-right text-gray-800">
                            {Object.values(annualData.monthly).reduce((s,m)=>s+m.purchaseAmt,0).toLocaleString()}
                          </td>
                          <td className="px-3 py-3 text-right text-orange-600">{annualData.totalSales.toLocaleString()}</td>
                          <td className="px-3 py-3 text-right text-emerald-700">{annualData.totalRevenue.toLocaleString()}</td>
                          <td className="px-3 py-3 text-right text-gray-800">{annualData.totalCost.toLocaleString()}</td>
                          <td className="px-3 py-3 text-right text-amber-700">{annualData.totalTax.toLocaleString()}</td>
                          <td className="px-3 py-3 text-right text-teal-700">{annualData.totalSubsidy.toLocaleString()}</td>
                          <td className="px-3 py-3 text-right text-gray-800">{Math.max(0, annualData.closingStock).toLocaleString()}</td>
                          <td className="px-3 py-3 text-center text-gray-400">—</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* 二、分品种年度汇总 */}
                <div>
                  <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center">
                    <span className="w-6 h-6 bg-gray-800 text-white rounded text-xs flex items-center justify-center mr-2 font-bold">二</span>
                    分品种年度经营汇总表
                  </h3>
                  {Object.keys(annualData.variety).length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-10 text-gray-300 bg-gray-50 rounded-xl border border-gray-100">
                      <i className="ri-inbox-line text-3xl mb-2"></i>
                      <p className="text-sm text-gray-400">{annualYear}年暂无经营数据</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto rounded-xl border border-gray-200">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-gray-100">
                            {['粮食品种','期初库存(吨)','收购量(吨)','收购金额(元)','销售量(吨)','营业收入(元)','毛利润(元)','应缴税款(元)','政府补贴(元)','期末库存(吨)'].map((h, i) => (
                              <th key={h} className={`border-b border-gray-200 px-3 py-3 text-gray-700 font-bold whitespace-nowrap ${i === 0 ? 'text-left' : 'text-right'}`}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(annualData.variety).map(([variety, data], idx) => {
                            const opening = OPENING_STOCK[variety] ?? 0;
                            const closing = Math.max(0, opening + data.purchase - data.sales);
                            const grossProfit = data.salesAmt - data.purchaseAmt;
                            return (
                              <tr key={variety} className={`border-b border-gray-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'} hover:bg-teal-50/20 transition-colors`}>
                                <td className="px-3 py-2.5 font-semibold text-gray-800">{variety}</td>
                                <td className="px-3 py-2.5 text-right text-gray-600">{opening.toLocaleString()}</td>
                                <td className="px-3 py-2.5 text-right text-teal-600 font-medium">{data.purchase.toLocaleString()}</td>
                                <td className="px-3 py-2.5 text-right text-gray-700">{data.purchaseAmt.toLocaleString()}</td>
                                <td className="px-3 py-2.5 text-right text-orange-500 font-medium">{data.sales.toLocaleString()}</td>
                                <td className="px-3 py-2.5 text-right text-emerald-600 font-medium">{data.salesAmt.toLocaleString()}</td>
                                <td className={`px-3 py-2.5 text-right font-semibold ${grossProfit >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                                  {grossProfit >= 0 ? '+' : ''}{grossProfit.toLocaleString()}
                                </td>
                                <td className="px-3 py-2.5 text-right text-amber-600">{data.tax.toLocaleString()}</td>
                                <td className="px-3 py-2.5 text-right text-teal-600">{data.subsidy.toLocaleString()}</td>
                                <td className="px-3 py-2.5 text-right font-bold text-gray-800">{closing.toLocaleString()}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                            <td className="px-3 py-3 text-gray-800">合计</td>
                            <td className="px-3 py-3 text-right text-gray-600">{annualData.totalOpeningStock.toLocaleString()}</td>
                            <td className="px-3 py-3 text-right text-teal-700">{annualData.totalPurchase.toLocaleString()}</td>
                            <td className="px-3 py-3 text-right text-gray-800">
                              {Object.values(annualData.variety).reduce((s,v)=>s+v.purchaseAmt,0).toLocaleString()}
                            </td>
                            <td className="px-3 py-3 text-right text-orange-600">{annualData.totalSales.toLocaleString()}</td>
                            <td className="px-3 py-3 text-right text-emerald-700">{annualData.totalRevenue.toLocaleString()}</td>
                            <td className={`px-3 py-3 text-right ${annualData.totalGrossProfit >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                              {annualData.totalGrossProfit >= 0 ? '+' : ''}{annualData.totalGrossProfit.toLocaleString()}
                            </td>
                            <td className="px-3 py-3 text-right text-amber-700">{annualData.totalTax.toLocaleString()}</td>
                            <td className="px-3 py-3 text-right text-teal-700">{annualData.totalSubsidy.toLocaleString()}</td>
                            <td className="px-3 py-3 text-right text-gray-800">{Math.max(0, annualData.closingStock).toLocaleString()}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>

                {/* 三、年度财务指标摘要 */}
                <div>
                  <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center">
                    <span className="w-6 h-6 bg-gray-800 text-white rounded text-xs flex items-center justify-center mr-2 font-bold">三</span>
                    年度财务指标摘要
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                      <div className="bg-gray-100 px-4 py-2.5 border-b border-gray-200">
                        <span className="text-xs font-bold text-gray-700">收入与成本</span>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {[
                          ['营业收入', annualData.totalRevenue, 'text-emerald-600'],
                          ['营业成本（收购金额）', annualData.totalCost, 'text-gray-700'],
                          ['毛利润', annualData.totalGrossProfit, annualData.totalGrossProfit >= 0 ? 'text-emerald-700 font-bold' : 'text-rose-600 font-bold'],
                        ].map(([label, val, cls]) => (
                          <div key={label as string} className="flex items-center justify-between px-4 py-2.5">
                            <span className="text-xs text-gray-500">{label as string}</span>
                            <span className={`text-sm ${cls as string}`}>¥{(val as number).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                      <div className="bg-gray-100 px-4 py-2.5 border-b border-gray-200">
                        <span className="text-xs font-bold text-gray-700">税款与补贴</span>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {[
                          ['应缴税款', annualData.totalTax, 'text-amber-600'],
                          ['政府补贴', annualData.totalSubsidy, 'text-teal-600'],
                          ['税后净利润', annualData.totalNetProfit, annualData.totalNetProfit >= 0 ? 'text-emerald-700 font-bold' : 'text-rose-600 font-bold'],
                        ].map(([label, val, cls]) => (
                          <div key={label as string} className="flex items-center justify-between px-4 py-2.5">
                            <span className="text-xs text-gray-500">{label as string}</span>
                            <span className={`text-sm ${cls as string}`}>¥{(val as number).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* 四、同比分析（新增） */}
                <div>
                  <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center">
                    <span className="w-6 h-6 bg-gray-800 text-white rounded text-xs flex items-center justify-center mr-2 font-bold">四</span>
                    同比分析（对比 {parseInt(annualYear, 10) - 1} 年度）
                  </h3>

                  {/* 核心指标同比 */}
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    {[
                      { label: '收购量', cur: annualData.totalPurchase, prev: prevYearData.totalPurchase, unit: '吨', icon: 'ri-arrow-down-circle-line' },
                      { label: '销售量', cur: annualData.totalSales, prev: prevYearData.totalSales, unit: '吨', icon: 'ri-arrow-up-circle-line' },
                      { label: '营业收入', cur: annualData.totalRevenue, prev: prevYearData.totalRevenue, unit: '元', icon: 'ri-money-cny-circle-line', isAmt: true },
                      { label: '毛利润', cur: annualData.totalGrossProfit, prev: prevYearData.totalGrossProfit, unit: '元', icon: 'ri-funds-line', isAmt: true },
                    ].map(item => {
                      const yoy = getYoyRate(item.cur, item.prev);
                      const isUp = yoy.rate > 0;
                      const isDown = yoy.rate < 0;
                      return (
                        <div key={item.label} className="bg-gray-50 rounded-xl border border-gray-200 p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-gray-500 font-medium">{item.label}</span>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${isUp ? 'bg-emerald-100 text-emerald-700' : isDown ? 'bg-rose-100 text-rose-700' : 'bg-gray-100 text-gray-500'}`}>
                              {isUp && <i className="ri-arrow-up-s-fill mr-0.5"></i>}
                              {isDown && <i className="ri-arrow-down-s-fill mr-0.5"></i>}
                              {yoy.label}
                            </span>
                          </div>
                          <div className="flex items-end justify-between">
                            <div>
                              <div className="text-base font-bold text-gray-800">
                                {item.isAmt ? `¥${(item.cur / 10000).toFixed(1)}万` : `${item.cur.toLocaleString()} ${item.unit}`}
                              </div>
                              <div className="text-xs text-gray-400 mt-0.5">
                                {parseInt(annualYear, 10) - 1}年：{item.isAmt ? `¥${(item.prev / 10000).toFixed(1)}万` : `${item.prev.toLocaleString()} ${item.unit}`}
                              </div>
                            </div>
                            {/* 迷你柱状对比 */}
                            <div className="flex items-end space-x-1 h-10">
                              <div className="w-4 bg-gray-300 rounded-t" style={{ height: `${Math.max(4, item.prev > 0 ? (item.prev / Math.max(item.cur, item.prev, 1)) * 40 : 4)}px` }}></div>
                              <div className={`w-4 rounded-t ${isUp ? 'bg-emerald-500' : isDown ? 'bg-rose-500' : 'bg-gray-400'}`} style={{ height: `${Math.max(4, item.cur > 0 ? (item.cur / Math.max(item.cur, item.prev, 1)) * 40 : 4)}px` }}></div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* 分品种同比表格 */}
                  <div className="overflow-x-auto rounded-xl border border-gray-200">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border-b border-gray-200 px-3 py-3 text-left text-gray-700 font-bold whitespace-nowrap" rowSpan={2}>粮食品种</th>
                          <th className="border-b border-gray-200 px-3 py-2 text-center text-gray-700 font-bold whitespace-nowrap" colSpan={3}>收购量（吨）</th>
                          <th className="border-b border-gray-200 px-3 py-2 text-center text-gray-700 font-bold whitespace-nowrap" colSpan={3}>销售量（吨）</th>
                          <th className="border-b border-gray-200 px-3 py-2 text-center text-gray-700 font-bold whitespace-nowrap" colSpan={3}>营业收入（元）</th>
                        </tr>
                        <tr className="bg-gray-50">
                          <th className="border-b border-gray-200 px-2 py-2 text-right text-gray-600 font-medium whitespace-nowrap">{annualYear}年</th>
                          <th className="border-b border-gray-200 px-2 py-2 text-right text-gray-600 font-medium whitespace-nowrap">{parseInt(annualYear, 10) - 1}年</th>
                          <th className="border-b border-gray-200 px-2 py-2 text-center text-gray-600 font-medium whitespace-nowrap">同比</th>
                          <th className="border-b border-gray-200 px-2 py-2 text-right text-gray-600 font-medium whitespace-nowrap">{annualYear}年</th>
                          <th className="border-b border-gray-200 px-2 py-2 text-right text-gray-600 font-medium whitespace-nowrap">{parseInt(annualYear, 10) - 1}年</th>
                          <th className="border-b border-gray-200 px-2 py-2 text-center text-gray-600 font-medium whitespace-nowrap">同比</th>
                          <th className="border-b border-gray-200 px-2 py-2 text-right text-gray-600 font-medium whitespace-nowrap">{annualYear}年</th>
                          <th className="border-b border-gray-200 px-2 py-2 text-right text-gray-600 font-medium whitespace-nowrap">{parseInt(annualYear, 10) - 1}年</th>
                          <th className="border-b border-gray-200 px-2 py-2 text-center text-gray-600 font-medium whitespace-nowrap">同比</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const allVarieties = new Set([...Object.keys(annualData.variety), ...Object.keys(prevYearData.variety)]);
                          return Array.from(allVarieties).map((variety, idx) => {
                            const cur = annualData.variety[variety] || { purchase: 0, purchaseAmt: 0, sales: 0, salesAmt: 0, tax: 0, subsidy: 0 };
                            const prev = prevYearData.variety[variety] || { purchase: 0, purchaseAmt: 0, sales: 0, salesAmt: 0, tax: 0, subsidy: 0 };
                            const purchaseYoy = getYoyRate(cur.purchase, prev.purchase);
                            const salesYoy = getYoyRate(cur.sales, prev.sales);
                            const revenueYoy = getYoyRate(cur.salesAmt, prev.salesAmt);
                            const renderYoy = (yoy: { rate: number; label: string }) => {
                              const isUp = yoy.rate > 0;
                              const isDown = yoy.rate < 0;
                              return (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${isUp ? 'bg-emerald-100 text-emerald-700' : isDown ? 'bg-rose-100 text-rose-700' : 'bg-gray-100 text-gray-500'}`}>
                                  {isUp && <i className="ri-arrow-up-s-fill mr-0.5"></i>}
                                  {isDown && <i className="ri-arrow-down-s-fill mr-0.5"></i>}
                                  {yoy.label}
                                </span>
                              );
                            };
                            return (
                              <tr key={variety} className={`border-b border-gray-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'} hover:bg-teal-50/20 transition-colors`}>
                                <td className="px-3 py-2.5 font-semibold text-gray-800">{variety}</td>
                                <td className="px-2 py-2.5 text-right text-teal-600 font-medium">{cur.purchase.toLocaleString()}</td>
                                <td className="px-2 py-2.5 text-right text-gray-500">{prev.purchase.toLocaleString()}</td>
                                <td className="px-2 py-2.5 text-center">{renderYoy(purchaseYoy)}</td>
                                <td className="px-2 py-2.5 text-right text-orange-500 font-medium">{cur.sales.toLocaleString()}</td>
                                <td className="px-2 py-2.5 text-right text-gray-500">{prev.sales.toLocaleString()}</td>
                                <td className="px-2 py-2.5 text-center">{renderYoy(salesYoy)}</td>
                                <td className="px-2 py-2.5 text-right text-emerald-600 font-medium">{cur.salesAmt.toLocaleString()}</td>
                                <td className="px-2 py-2.5 text-right text-gray-500">{prev.salesAmt.toLocaleString()}</td>
                                <td className="px-2 py-2.5 text-center">{renderYoy(revenueYoy)}</td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                      <tfoot>
                        <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                          <td className="px-3 py-3 text-gray-800">合计</td>
                          <td className="px-2 py-3 text-right text-teal-700">{annualData.totalPurchase.toLocaleString()}</td>
                          <td className="px-2 py-3 text-right text-gray-500">{prevYearData.totalPurchase.toLocaleString()}</td>
                          <td className="px-2 py-3 text-center">
                            {(() => { const y = getYoyRate(annualData.totalPurchase, prevYearData.totalPurchase); return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${y.rate > 0 ? 'bg-emerald-100 text-emerald-700' : y.rate < 0 ? 'bg-rose-100 text-rose-700' : 'bg-gray-100 text-gray-500'}`}>{y.label}</span>; })()}
                          </td>
                          <td className="px-2 py-3 text-right text-orange-600">{annualData.totalSales.toLocaleString()}</td>
                          <td className="px-2 py-3 text-right text-gray-500">{prevYearData.totalSales.toLocaleString()}</td>
                          <td className="px-2 py-3 text-center">
                            {(() => { const y = getYoyRate(annualData.totalSales, prevYearData.totalSales); return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${y.rate > 0 ? 'bg-emerald-100 text-emerald-700' : y.rate < 0 ? 'bg-rose-100 text-rose-700' : 'bg-gray-100 text-gray-500'}`}>{y.label}</span>; })()}
                          </td>
                          <td className="px-2 py-3 text-right text-emerald-700">{annualData.totalRevenue.toLocaleString()}</td>
                          <td className="px-2 py-3 text-right text-gray-500">{prevYearData.totalRevenue.toLocaleString()}</td>
                          <td className="px-2 py-3 text-center">
                            {(() => { const y = getYoyRate(annualData.totalRevenue, prevYearData.totalRevenue); return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${y.rate > 0 ? 'bg-emerald-100 text-emerald-700' : y.rate < 0 ? 'bg-rose-100 text-rose-700' : 'bg-gray-100 text-gray-500'}`}>{y.label}</span>; })()}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {/* 同比趋势说明 */}
                  <div className="mt-3 flex items-center space-x-4 text-xs text-gray-400">
                    <span className="flex items-center"><span className="w-3 h-3 bg-gray-300 rounded-sm mr-1"></span>{parseInt(annualYear, 10) - 1}年</span>
                    <span className="flex items-center"><span className="w-3 h-3 bg-emerald-500 rounded-sm mr-1"></span>{annualYear}年（增长）</span>
                    <span className="flex items-center"><span className="w-3 h-3 bg-rose-500 rounded-sm mr-1"></span>{annualYear}年（下降）</span>
                  </div>
                </div>

                {/* 保密声明 */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-800 leading-relaxed">
                  <i className="ri-shield-keyhole-line mr-1"></i>
                  <strong>保密声明：</strong>本汇总表数据涉及商业秘密，严格按照相关保密规定执行。报送的基本数据须与生产经营实际相符，与粮食经营台账相符。月度内没有发生业务的，月度末结转上月末相关指标数据。
                </div>

                {/* 签章区域 */}
                <div className="border-t-2 border-gray-800 pt-5 mt-2">
                  <div className="grid grid-cols-3 gap-8 text-sm text-gray-700">
                    <div>
                      <div className="font-medium text-gray-800 mb-5">制表人（签字）：</div>
                      <div className="border-b border-gray-400 pb-1"></div>
                    </div>
                    <div>
                      <div className="font-medium text-gray-800 mb-5">企业负责人（签字）：</div>
                      <div className="border-b border-gray-400 pb-1"></div>
                    </div>
                    <div>
                      <div className="font-medium text-gray-800 mb-5">（企业公章）</div>
                      <div className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                        <span className="text-xs text-gray-400">公章位置</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right text-sm text-gray-600 mt-4">
                    填报日期：{new Date().getFullYear()} 年 {new Date().getMonth() + 1} 月 {new Date().getDate()} 日
                  </div>
                </div>
              </div>
            </div>

            {/* 底部操作栏 */}
            <div className="px-8 py-4 border-t border-gray-200 flex items-center justify-between bg-white">
              <div className="flex items-center text-sm text-gray-500">
                <i className="ri-information-line mr-1.5 text-teal-600"></i>
                {annualYear}年度 · 共 {Object.keys(annualData.variety).length} 个品种 · 数据来源：台账实时同步
              </div>
              <div className="flex items-center space-x-3">
                <button onClick={() => setAnnualModalOpen(false)}
                  className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 text-sm font-medium whitespace-nowrap cursor-pointer">
                  关闭
                </button>
                <button onClick={handleExportAnnualPDF} disabled={exportingPDF}
                  className="px-8 py-2.5 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-xl hover:shadow-lg text-sm font-medium whitespace-nowrap cursor-pointer disabled:opacity-60 flex items-center">
                  {exportingPDF ? <><i className="ri-loader-4-line mr-2 animate-spin"></i>导出中...</> : <><i className="ri-file-pdf-2-line mr-2"></i>导出PDF</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 购销存月报表弹窗 */}
      <MonthlyReportModal open={monthlyModalOpen} onClose={() => setMonthlyModalOpen(false)} records={financeRecords} />

      {/* 客户往来统计表弹窗 */}
      <CustomerReportModal open={customerModalOpen} onClose={() => setCustomerModalOpen(false)} records={financeRecords} customers={customers} />

      {/* 质量指标汇总表弹窗 */}
      <QualityReportModal open={qualityModalOpen} onClose={() => setQualityModalOpen(false)} records={financeRecords} />
    </div>
  );
}
